import { supabase } from './supabase';

// utils/formSubmissionHelper.ts

export interface FormSubmission {
  formType: 'conversion_copy' | 'brand_scaling';
  timestamp: Date;
  data: Record<string, any>;
}

export interface FormSubmissionData {
  formType: 'conversion_copy' | 'brand_scaling';
  email: string;
  name: string;
  formData: Record<string, any>;
}

/**
 * Submit form data to Supabase database
 */
export const submitForm = async (data: FormSubmissionData): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('form_submissions')
      .insert({
        form_type: data.formType,
        email: data.email,
        name: data.name,
        form_data: data.formData,
        created_at: new Date().toISOString()
      });

    if (error) {
      console.error('Supabase error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Form submission error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to submit form' 
    };
  }
};

/**
 * Submit form to backend AND notify Cleo chatbot
 * This replaces your existing form submission calls
 */
export const submitFormWithCleoNotification = async (
  formType: 'conversion_copy' | 'brand_scaling',
  email: string,
  name: string,
  formData: Record<string, any>
): Promise<{ success: boolean; error?: string }> => {
  try {
    // First, submit to your backend
    const backendData: FormSubmissionData = {
      formType,
      email,
      name,
      formData
    };

    const result = await submitForm(backendData);

    if (result.success) {
      // Only notify Cleo if backend submission was successful
      notifyCleoOfFormSubmission(formType, {
        email,
        name,
        ...formData
      });

      return { success: true };
    } else {
      return { success: false, error: result.error };
    }
  } catch (error) {
    console.error('Form submission error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to submit form' 
    };
  }
};

/**
 * Internal function to notify Cleo chatbot about form submission
 * This gets called automatically by submitFormWithCleoNotification
 */
const notifyCleoOfFormSubmission = (
  formType: 'conversion_copy' | 'brand_scaling',
  formData: Record<string, any>
) => {
  const submission: FormSubmission = {
    formType,
    timestamp: new Date(),
    data: formData
  };

  // Dispatch custom event that Cleo will listen for
  const event = new CustomEvent('formSubmitted', {
    detail: submission
  });

  window.dispatchEvent(event);
};

// Example usage in your form components:

// For ConversionCopyCommand.tsx:
/*
import { submitFormWithCleoNotification } from '../utils/formSubmissionHelper';

const handleSubmit = async (formData) => {
  try {
    const result = await submitFormWithCleoNotification(
      'conversion_copy',
      formData.email,
      formData.name,
      {
        businessName: formData.businessName,
        projectType: formData.projectType,
        timeline: formData.timeline,
        budget: formData.budget,
        websiteUrl: formData.websiteUrl,
        // Include any other form fields
      }
    );

    if (result.success) {
      // Form submitted successfully and Cleo has been notified
      console.log('Form submitted successfully!');
      // Reset form or show success message
    } else {
      // Handle error
      console.error('Submission failed:', result.error);
    }
  } catch (error) {
    console.error('Unexpected error:', error);
  }
};
*/

// For RevenueDigitalWarfare.tsx:
/*
import { submitFormWithCleoNotification } from '../utils/formSubmissionHelper';

const handleSubmit = async (formData) => {
  try {
    const result = await submitFormWithCleoNotification(
      'brand_scaling',
      formData.email,
      formData.name,
      {
        companyName: formData.companyName,
        currentRevenue: formData.currentRevenue,
        targetGoals: formData.targetGoals,
        industry: formData.industry,
        challenges: formData.challenges,
        // Include any other form fields
      }
    );

    if (result.success) {
      // Form submitted successfully and Cleo has been notified
      console.log('Form submitted successfully!');
      // Reset form or show success message
    } else {
      // Handle error
      console.error('Submission failed:', result.error);
    }
  } catch (error) {
    console.error('Unexpected error:', error);
  }
};
*/